/**
 * Pure computation hook for the Monthly Feed Budget Planner.
 *
 * Computes:
 * - Per-animal-group: demand, supply, deficit, supplements
 * - Farm totals: aggregated across all groups
 * - Running pasture cover
 */

import { useMemo } from "react";
import {
  DEFAULT_FIELD_FEEDING_UTILISATION_PERCENT,
  DEFAULT_GRAZING_DAYS,
  DEFAULT_GRAZING_FIELD_UTILISATION_PERCENT,
  DEFAULT_UTILISATION_PERCENT,
  getDefaultHousedUtilisation,
} from "./constants";
import type {
  AnimalGroupBudget,
  AnimalGroupComputedRow,
  FarmTotalComputedRow,
  FeedBudgetPlan,
  HousingStatus,
  StockClassComputedMonth,
  StockClassMonthlyPlan,
} from "./types";

export interface FeedBudgetComputedData {
  farmTotalRows: FarmTotalComputedRow[];
  /** Map from animalGroupId to its 12 computed rows */
  animalGroupRows: Map<string, AnimalGroupComputedRow[]>;
  /** Map from stockClassId to its 12 computed months */
  stockClassComputed: Map<string, StockClassComputedMonth[]>;
  /** Whether the plan covers a second calendar year (any stock class has yearOffset:1 entries) */
  hasYear1: boolean;
  /** Year-1 computed rows per animal group (present when hasYear1 is true) */
  animalGroupRowsYear1: Map<string, AnimalGroupComputedRow[]>;
  /** Year-1 computed stock class months (present when hasYear1 is true) */
  stockClassComputedYear1: Map<string, StockClassComputedMonth[]>;
  /** Farm-total rows for year 1 */
  farmTotalRowsYear1: FarmTotalComputedRow[];
}

function computeStockClassMonths(
  plan: StockClassMonthlyPlan,
  targetYearOffset: 0 | 1 = 0
): StockClassComputedMonth[] {
  return plan.months
    .filter((e) => (e.yearOffset ?? 0) === targetYearOffset)
    .map((entry) => {
      const dailyAllocationKgDM =
        (entry.averageWeightKg * entry.allocationPercent) / 100;
      return {
        month: entry.month,
        animalCount: entry.animalCount,
        averageWeightKg: entry.averageWeightKg,
        allocationPercent: entry.allocationPercent,
        dailyAllocationKgDM,
        totalDailyRequirementKgDM: dailyAllocationKgDM * entry.animalCount,
      };
    });
}

function computeAnimalGroupRows(
  agb: AnimalGroupBudget,
  grazingMonths: FeedBudgetPlan["grazingMonths"],
  stockClassComputed: Map<string, StockClassComputedMonth[]>,
  actualGrowthRatesKgHaDay: number[],
  currentMonth: number,
  yearOffset: 0 | 1 = 0
): AnimalGroupComputedRow[] {
  return Array.from({ length: 12 }, (_, month) => {
    const grazing =
      grazingMonths.find(
        (gm) => gm.month === month && (gm.yearOffset ?? 0) === yearOffset
      ) ?? grazingMonths.find((gm) => gm.month === month);
    // Year-1 supplement plans are not yet set — default to no supplements
    const supplement =
      yearOffset === 0
        ? agb.supplementPlans.find((sp) => sp.month === month) ??
          agb.supplementPlans[month]
        : { month, utilisationPercent: 0, amounts: {} };
    const grazingDays = DEFAULT_GRAZING_DAYS[month];

    // Sum demand across this group's stock classes
    let totalDemandKgDay = 0;
    for (const scPlan of agb.stockClassPlans) {
      const scMonths = stockClassComputed.get(scPlan.stockClassId);
      const scMonth = scMonths?.find((m) => m.month === month);
      if (scMonth) {
        totalDemandKgDay += scMonth.totalDailyRequirementKgDM;
      }
    }

    // Housing status — use yearOffset-matched entries
    let grazingCount = 0;
    let housedCount = 0;
    let classesWithMonthEntry = 0;
    for (const scPlan of agb.stockClassPlans) {
      const entry = scPlan.months.find(
        (m) => m.month === month && (m.yearOffset ?? 0) === yearOffset
      );
      // Ignore stock classes that do not have data for this year-offset/month.
      // Counting missing entries as grazing keeps the UI stuck on "Grazing"
      // even when visible classes were toggled to housed.
      if (!entry) continue;
      classesWithMonthEntry += 1;
      if (entry.isGrazing === false) housedCount++;
      else grazingCount++;
    }
    const housingStatus: HousingStatus =
      classesWithMonthEntry === 0
        ? "grazing"
        : housedCount === 0
        ? "grazing"
        : grazingCount === 0
        ? "housed"
        : "partially-grazing";
    const configuredArea =
      agb.monthlyGrazingAreaHa?.[month] ?? agb.grazingAreaHa;
    const area = housingStatus === "housed" ? 0 : configuredArea;
    const demandKgHaDay = area > 0 ? totalDemandKgDay / area : 0;

    const utilisationPercent =
      agb.grazingUtilisationPercent ??
      grazing?.utilisationPercent ??
      DEFAULT_GRAZING_FIELD_UTILISATION_PERCENT;
    // Actual growth rates only apply to year-0 past months
    const hasActual =
      yearOffset === 0 &&
      month <= currentMonth &&
      (actualGrowthRatesKgHaDay[month] ?? 0) > 0;
    const effectiveGrowthRate = hasActual
      ? actualGrowthRatesKgHaDay[month]
      : grazing?.growthRateKgHaDay ?? 0;
    const supplyKgHaDay =
      housingStatus === "housed"
        ? 0
        : (effectiveGrowthRate * utilisationPercent) / 100;
    const differenceKgHaDay = supplyKgHaDay - demandKgHaDay;

    // Supplements
    const fieldUtil =
      agb.fieldFeedingUtilisationPercent ??
      DEFAULT_FIELD_FEEDING_UTILISATION_PERCENT;
    const getUtil = (feedTypeId: string) =>
      housingStatus === "housed"
        ? agb.feedTypeUtilisation?.[feedTypeId] ??
          getDefaultHousedUtilisation(feedTypeId)
        : fieldUtil;

    let totalSupplKgEffective = 0;
    for (const feedType of agb.supplementFeedTypes ?? []) {
      const amount = supplement.amounts?.[feedType.id] ?? 0;
      if (amount === 0) continue;
      totalSupplKgEffective +=
        amount *
        1000 *
        feedType.substitutionRate *
        (getUtil(feedType.id) / 100);
    }
    const supplementArea = configuredArea;
    const supplementKgHaDay =
      supplementArea > 0 && grazingDays > 0
        ? totalSupplKgEffective / (supplementArea * grazingDays)
        : 0;

    const forageCoveragePercent =
      demandKgHaDay > 0
        ? Math.min(999, (supplyKgHaDay / demandKgHaDay) * 100)
        : 100;

    return {
      month,
      grazingAreaHa: area,
      totalDemandKgDay,
      demandKgHaDay,
      supplyKgHaDay,
      differenceKgHaDay,
      supplementKgHaDay,
      netDifferenceKgHaDay: differenceKgHaDay + supplementKgHaDay,
      housingStatus,
      forageCoveragePercent,
    };
  });
}

function computeFarmTotalRows(
  plan: FeedBudgetPlan,
  animalGroupRows: Map<string, AnimalGroupComputedRow[]>,
  actualGrowthRatesKgHaDay: number[],
  currentMonth: number,
  initialPastureCover: number,
  yearOffset: 0 | 1 = 0
): FarmTotalComputedRow[] {
  let runningPastureCover = initialPastureCover;
  return Array.from({ length: 12 }, (_, month) => {
    const grazing =
      plan.grazingMonths.find(
        (gm) => gm.month === month && (gm.yearOffset ?? 0) === yearOffset
      ) ?? plan.grazingMonths.find((gm) => gm.month === month);
    const grazingDays = DEFAULT_GRAZING_DAYS[month];

    let totalDemandKgDay = 0;
    let totalSupplementKgDay = 0;
    let totalGrazingAreaHa = 0;

    for (const agb of plan.animalGroupBudgets) {
      const agRow = animalGroupRows.get(agb.animalGroupId)?.[month];
      if (agRow) {
        const monthArea = agRow.grazingAreaHa;
        totalDemandKgDay += agRow.totalDemandKgDay;
        totalSupplementKgDay += agRow.supplementKgHaDay * monthArea;
        totalGrazingAreaHa += monthArea;
      }
    }

    const utilisationPct =
      grazing?.utilisationPercent ?? DEFAULT_UTILISATION_PERCENT;
    const hasActual =
      yearOffset === 0 &&
      month <= currentMonth &&
      (actualGrowthRatesKgHaDay[month] ?? 0) > 0;
    const effectiveGrowthRate = hasActual
      ? actualGrowthRatesKgHaDay[month]
      : grazing?.growthRateKgHaDay ?? 0;
    const supplyKgHaDay = (effectiveGrowthRate * utilisationPct) / 100;
    const totalSupplyKgDay = supplyKgHaDay * totalGrazingAreaHa;
    const demandKgHaDay =
      totalGrazingAreaHa > 0 ? totalDemandKgDay / totalGrazingAreaHa : 0;
    const differenceKgHaDay = supplyKgHaDay - demandKgHaDay;
    const supplementKgHaDay =
      totalGrazingAreaHa > 0 ? totalSupplementKgDay / totalGrazingAreaHa : 0;
    const netDifferenceKgHaDay = differenceKgHaDay + supplementKgHaDay;

    runningPastureCover += netDifferenceKgHaDay * grazingDays;

    return {
      month,
      growthRateKgHaDay: effectiveGrowthRate,
      utilisationPercent: grazing?.utilisationPercent ?? 0,
      totalGrazingAreaHa,
      totalDemandKgDay,
      totalSupplyKgDay,
      demandKgHaDay,
      supplyKgHaDay,
      differenceKgHaDay,
      totalSupplementKgDay,
      netDifferenceKgHaDay,
      averagePastureCoverKgHa: runningPastureCover,
    };
  });
}

export function computeAllRows(
  plan: FeedBudgetPlan,
  actualGrowthRatesKgHaDay: number[] = [],
  currentMonth: number = -1
): FeedBudgetComputedData {
  // Detect whether any stock class has year-1 entries
  const hasYear1 = plan.animalGroupBudgets.some((agb) =>
    agb.stockClassPlans.some((sc) => sc.months.some((m) => m.yearOffset === 1))
  );

  // 1. Compute year-0 stock classes
  const stockClassComputed = new Map<string, StockClassComputedMonth[]>();
  const stockClassComputedYear1 = new Map<string, StockClassComputedMonth[]>();
  for (const agb of plan.animalGroupBudgets) {
    for (const scPlan of agb.stockClassPlans) {
      stockClassComputed.set(
        scPlan.stockClassId,
        computeStockClassMonths(scPlan, 0)
      );
      if (hasYear1) {
        stockClassComputedYear1.set(
          scPlan.stockClassId,
          computeStockClassMonths(scPlan, 1)
        );
      }
    }
  }

  // 2. Compute per-animal-group rows for year 0 (and optionally year 1)
  const animalGroupRows = new Map<string, AnimalGroupComputedRow[]>();
  const animalGroupRowsYear1 = new Map<string, AnimalGroupComputedRow[]>();
  for (const agb of plan.animalGroupBudgets) {
    animalGroupRows.set(
      agb.animalGroupId,
      computeAnimalGroupRows(
        agb,
        plan.grazingMonths,
        stockClassComputed,
        actualGrowthRatesKgHaDay,
        currentMonth,
        0
      )
    );
    if (hasYear1) {
      animalGroupRowsYear1.set(
        agb.animalGroupId,
        computeAnimalGroupRows(
          agb,
          plan.grazingMonths,
          stockClassComputedYear1,
          actualGrowthRatesKgHaDay,
          currentMonth,
          1
        )
      );
    }
  }

  // 3. Compute farm-wide totals for year 0
  const farmTotalRows = computeFarmTotalRows(
    plan,
    animalGroupRows,
    actualGrowthRatesKgHaDay,
    currentMonth,
    plan.initialPastureCoverKgHa,
    0
  );

  // 4. Compute year-1 farm totals, starting from where year-0 ended
  const year0EndCover =
    farmTotalRows[farmTotalRows.length - 1]?.averagePastureCoverKgHa ??
    plan.initialPastureCoverKgHa;
  const farmTotalRowsYear1 = hasYear1
    ? computeFarmTotalRows(
        plan,
        animalGroupRowsYear1,
        actualGrowthRatesKgHaDay,
        currentMonth,
        year0EndCover,
        1
      )
    : [];

  return {
    farmTotalRows,
    farmTotalRowsYear1,
    animalGroupRows,
    animalGroupRowsYear1,
    stockClassComputed,
    stockClassComputedYear1,
    hasYear1,
  };
}

/**
 * React hook that memoizes the feed budget calculations.
 */
export function useFeedBudgetCalculations(
  plan: FeedBudgetPlan | null,
  actualGrowthRatesKgHaDay: number[] = [],
  currentMonth: number = -1
): FeedBudgetComputedData | null {
  return useMemo(() => {
    if (!plan) return null;
    return computeAllRows(plan, actualGrowthRatesKgHaDay, currentMonth);
  }, [plan, actualGrowthRatesKgHaDay, currentMonth]);
}
